<template>
  <v-container fluid>
    <page-title icon="fas fa-fw fa-envelope">
      Email Templates
      <template v-slot:buttons>
        <v-btn :to="{name: 'admin.share_template.create'}"
               color="primary">
          New Template
        </v-btn>
      </template>
    </page-title>

    <div ref="container"
         class="d-flex"
         style="width: 100%;">
      <div class="mr-2"
           style="width: 260px;">
        <v-card class="mb-4">
          <v-card-title>
            <v-icon left>
              {{ isFilterApplied ? 'fas fa-fw fa-filter' : 'fal fa-fw fa-filter' }}
            </v-icon>
            {{ isFilterApplied ? 'Filtered Templates' : 'Filter Templates' }}
          </v-card-title>
          <v-card-text class="pb-0">

            <v-autocomplete v-if="showFolderField"
                            v-model="form.folder_id"
                            label="Folder"
                            :items="fullFolderTree"
                            item-value="id"
                            :item-text="folderPath"
                            outlined
                            dense>
            </v-autocomplete>

            <v-text-field v-model="form.name"
                          label="Name"
                          placeholder="Name contains"
                          outlined
                          dense>
            </v-text-field>

            <v-text-field v-model="form.subject"
                          label="Subject"
                          placeholder="Subject contains"
                          outlined
                          dense>
            </v-text-field>

            <v-text-field v-model="form.asset_id"
                          label="Asset ID"
                          placeholder="Asset ID contains"
                          outlined
                          dense>
            </v-text-field>

            <v-autocomplete v-model="form.group_id"
                            label="Group with access"
                            :items="allGroups"
                            item-value="id"
                            item-text="name"
                            outlined
                            dense>
            </v-autocomplete>

            <v-autocomplete v-if="team.brands_enabled"
                            v-model="form.brand_id"
                            label="Brand"
                            :items="allBrands"
                            item-value="id"
                            item-text="name"
                            outlined
                            dense>
            </v-autocomplete>

            <v-select v-model="form.editor"
                      label="Editor"
                      :items="editors"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense>
            </v-select>

            <v-select v-model="form.is_featured"
                      label="Featured"
                      :items="yesNoOptions"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense>
            </v-select>

            <v-select v-if="isCampaignsEnabled"
                      v-model="form.is_campaign"
                      label="Campaign"
                      :items="yesNoOptions"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense>
            </v-select>

            <v-select v-if="isQuickShareEnabled"
                      v-model="form.is_quick_share"
                      label="Quick Share"
                      :items="yesNoOptions"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense>
            </v-select>

            <v-autocomplete v-model="form.locale"
                            label="Language"
                            :items="allLocales"
                            item-value="id"
                            item-text="name"
                            outlined
                            dense>
            </v-autocomplete>

            <v-select v-model="form.status"
                      label="Status"
                      :items="statuses"
                      item-value="id"
                      item-text="name"
                      outlined
                      dense>
            </v-select>
          </v-card-text>
          <v-card-actions class="px-4">
            <v-btn @click.prevent="resetForm"
                   color="primary"
                   block
                   tile>
              Reset Filter
            </v-btn>
          </v-card-actions>
        </v-card>
      </div>


      <div v-if="loading"
           class="flex-grow-1">
        <v-card class="mb-4"
                :loading="loading"
                loading-height="32">
          <v-card-title>

          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="text-center">
            <div>
              Loading templates
            </div>
          </v-card-text>
        </v-card>
      </div>
      <div v-else-if="filteredTemplates.length > 0"
           :style="tableStyle">
        <v-card>
          <v-card-title>
            {{ filteredTemplates.length | pluralize('Template') }}
          </v-card-title>
          <v-divider></v-divider>
          <ShareTemplatesTable :loading="loading"
                               :fixed-header="true"
                               :templates="filteredTemplates">
          </ShareTemplatesTable>
        </v-card>
      </div>
      <div v-else
           class="flex-grow-1">
        <v-card class="mb-4">
          <v-card-title>
            <template v-if="currentFolder">
              <v-icon left>
                {{ currentFolder.icon ? `fas fa-fw ${currentFolder.icon}` : 'fas fa-fw-fa fa-folder-open' }}
              </v-icon>
              {{ currentFolder.name }}
            </template>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="text-center">
            <p v-if="currentFolder">
              There are no templates matching your filter criteria in the <strong>{{ currentFolder.name }}</strong> folder.
            </p>
            <p v-else>
              There are no templates matching your filter criteria.
            </p>
            <div>
              <v-btn @click.prevent="resetForm"
                     color="primary"
                     tile>
                Reset Filter
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </v-container>
</template>
<script>
import {clone, find, isNull, isObject, orderBy} from 'lodash';
import {mapGetters} from 'vuex';
import ShareTemplatesTable from './components/ShareTemplatesTable';
import PageTitle from '../../components/PageTitle.vue';
import saveState from 'vue-save-state';

const FORM_KEY = 'admin_share_template_columns';
const FORM_VERSION = 2;

function initForm() {
  return {
    folder_id: '',
    displayOrder: 'alphabetical',
    asset_id: '',
    locale: '', // MLD-5385
    brand_id: '', // MLD-4796
    editor: '',
    group_id: '',
    status: 'Active',
    is_campaign: '',
    is_featured: '',
    is_quick_share: '',
    name: '',
    subject: '',
    user_id: '',
    version: FORM_VERSION,
  }
}

export default {
  components: {PageTitle, ShareTemplatesTable},
  mixins: [saveState], // MLD-6444
  data() {
    return {
      displayOrders: [
        {
          id: 'engagement',
          name: 'Engagement - high to low',
        },
        {
          id: 'featured',
          name: 'Featured and alphabetical',
        },
        {
          id: 'chronological',
          name: 'Newest to oldest',
        },
        {
          id: 'alphabetical',
          name: 'Alphabetical',
        },
      ],
      editors: [
        {id: '', name: 'Any'},
        {id: 'tinymce', name: 'TinyMCE'},
        {id: 'unlayer', name: 'Unlayer'},
        {id: 'raw', name: 'HTML'},
      ],
      form: initForm(),
      loading: false,
      yesNoOptions: [
        {id: '', name: 'Yes or No'},
        {id: 'yes', name: 'Yes'},
        {id: 'no', name: 'No'},
      ],
      statuses: [
        {id: '', name: 'Active or Inactive',},
        {id: 'Active', name: 'Active'},
        {id: 'Inactive', name: 'Inactive'},
      ]
    }
  },
  computed: {
    ...mapGetters([
      'activeFolders',
      'folder',
      'folderTree',
      'groups', // MLD-6
      'homeFolder',
      'isCampaignsEnabled', // MLD-3917
      'isQuickShareEnabled', // MLD-3917
      'locales', // MLD-5385
      'openAllFolders',
      'rootFolders',
      'shareTemplates',
      'team',
    ]),
    ...mapGetters('content', [
      'brands', // MLD-4796
    ]),
    currentFolder() {
      if (this.form.folder_id) {
        return find(this.fullFolderTree, f => f.id === this.form.folder_id);
      }
      return null;
    },
    fullFolderTree() {
      return [
        {
          id: '',
          name: 'Any Folder',
        },
        this.homeFolder,
      ].concat(this.folderTree);
    },
    allGroups() {
      const any = {id: '', name: 'Any'};
      return [any].concat(this.groups);
    },
    showFolderField() { // MLD-6970 I re-enabled this for teams using the Email plugins.
      return this.team
        && (this.team.email_plugins_enabled || this.team.outlook_plugin_enabled)
        && this.rootFolders?.length > 1;
    },
    tableStyle() {
      return 'flex: 1 0 0; overflow-y: auto;';
      //return 'width: calc(100% - 260px)';
    },
    isFilterApplied() { // MLD-6444
      const originalForm = JSON.stringify(initForm());
      const currentForm = JSON.stringify(this.form);
      return originalForm !== currentForm;
    },
    allLocales() { // MLD-5385
      const locales = clone(this.locales);
      locales.unshift({
        id: '',
        name: 'Any',
      });
      return locales;
    },
    allBrands() { // MLD-4796
      const brands = clone(this.brands);
      brands.unshift({
        id: '',
        name: 'Any',
      })
      return brands;
    },
    filteredTemplates() {
      const filter = this.form;
      const templates = this.shareTemplates.filter(t => {
        if (filter.status && filter.status !== t.status) {
          return false;
        }

        if (filter.is_featured) {
          if (filter.is_featured === 'yes' && !t.is_featured) {
            return false;
          }
          if (filter.is_featured === 'no' && t.is_featured) {
            return false;
          }
        }
        if (filter.is_campaign) {
          if (filter.is_campaign === 'yes' && !t.is_campaign) {
            return false;
          }
          if (filter.is_campaign === 'no' && t.is_campaign) {
            return false;
          }
        }
        if (filter.is_quick_share) {
          if (filter.is_quick_share === 'yes' && !t.is_quick_share) {
            return false;
          }
          if (filter.is_quick_share === 'no' && t.is_quick_share) {
            return false;
          }
        }

        if (filter.editor && filter.editor !== t.editor) {
          return false;
        }

        if (filter.name && t.name && !t.name.toLowerCase().includes(filter.name.toLowerCase())) {
          return false;
        }

        if (filter.asset_id && !t.asset_id) {
          return false;
        }

        if (filter.asset_id && !t.asset_id.toLowerCase().includes(filter.asset_id.toLowerCase())) {
          return false;
        }

        if (filter.brand_id && !t.brand_id) { // MLD-4796
          return false;
        }
        if (filter.brand_id && t.brand_id && t.brand_id !== filter.brand_id) { // MLD-4796
          return false;
        }

        if (filter.subject && t.subject && !t.subject.toLowerCase().includes(filter.subject.toLowerCase())) {
          return false;
        }

        const templateGroupIDs = t.groups.map(g => g.id);
        if (filter.group_id && templateGroupIDs.length > 0 && templateGroupIDs.includes(filter.group_id) === false) {
          return false;
        }

        if (filter.user_id && t.user_id !== filter.user_id) {
          return false;
        }

        /*if (this.activeFolders && this.activeFolders.length && this.activeFolders[0] !== -1) {
          if (!t.folder_id) {
            return false;
          }
          return this.activeFolders.includes(t.folder_id);
        }*/
        if (this.form.folder_id) {
          if (this.form.folder_id === -1 && isNull(t.folder_id)) {
            return true;
          } else {
            return this.form.folder_id === t.folder_id;
          }
        }

        if (filter.locale && filter.locale !== t.locale) { // MLD-5385
          return false;
        }

        return true;
      })

      if (this.form.displayOrder === 'engagement') {
        return orderBy(templates, ['metrics.engagement_score', 'metrics.delivered_count'], ['desc', 'desc']);
      } else if (this.form.displayOrder === 'display') {
        return orderBy(templates, ['is_active_released_not_expired', 'is_featured', 'slug'], ['desc', 'desc', 'asc']);
      } else if (this.form.displayOrder === 'chronological') {
        return orderBy(templates, ['id', 'slug'], ['desc', 'asc']);
      }
      return orderBy(templates, ['slug'], ['asc']);
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    folderPath(folder) {

      const parts = [];
      folder.ancestors?.forEach(ancestor => {
        parts.push(ancestor.name);
      })
      parts.push(folder.name);
      return parts.join(' > ');
    },
    getSaveStateConfig() { // MLD-6444
      return {
        cacheKey: FORM_KEY,
        saveProperties: [
          'form',
        ],
        onLoad: (key, value) => { // MLD-1864
          if (key === 'form' && isObject(value)) {
            if (value.version !== FORM_VERSION) {
              return initForm();
            }
          }
          return value;
        }
      }
    },
    fetchData() {
      //this.$store.dispatch('fetchShareTemplates');
    },
    updateFolder(activeFolders) { // MLD-4796, MLD-4976
      this.$store.dispatch('updateActiveFolders', activeFolders);
    },
    resetForm() {
      this.loading = true;
      this.clearSavedState();
      this.form = initForm();
      this.$store.dispatch('updateActiveFolders', -1);
      this.loading = false;
    },
  }
}
</script>
